import { useState, useEffect, useCallback } from 'react';

const RECENT_SEARCHES_KEY = 'loci_recent_searches';
const MAX_RECENT_SEARCHES = 5;

export interface RecentSearch {
  query: string;
  timestamp: number;
}

export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // Load recent searches from session storage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = sessionStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as RecentSearch[];
        setRecentSearches(parsed);
      }
    } catch (error) {
      console.error('[useRecentSearches] Error loading recent searches:', error);
      setRecentSearches([]);
    }
  }, []);

  // Add a search to recent searches
  const addRecentSearch = useCallback((query: string) => {
    if (!query.trim()) return;

    const normalizedQuery = query.trim().toLowerCase();

    setRecentSearches((prev) => {
      // Remove duplicate if exists
      const filtered = prev.filter((search) => search.query.toLowerCase() !== normalizedQuery);

      // Add new search at the beginning
      const updated = [{ query: query.trim(), timestamp: Date.now() }, ...filtered].slice(
        0,
        MAX_RECENT_SEARCHES,
      );

      // Save to session storage
      try {
        sessionStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('[useRecentSearches] Error saving recent searches:', error);
      }

      return updated;
    });
  }, []);

  // Clear all recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      sessionStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
      console.error('[useRecentSearches] Error clearing recent searches:', error);
    }
  }, []);

  // Get recent search queries as string array
  const getRecentQueries = useCallback((): string[] => {
    return recentSearches.map((search) => search.query);
  }, [recentSearches]);

  return {
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
    getRecentQueries,
    hasRecentSearches: recentSearches.length > 0,
  };
}
